// ============================================================
// DisbursementModal.tsx — Invoicing Stage 1: record / edit a disbursement
// on a case. Category → default VAT treatment (court fee / police → none,
// counsel → at cost, medical / expert / travel / other → 20%), editable.
// The browser sends net pence + treatment (+ the supplier's VAT for "at
// cost"); the SERVER derives the stored VAT (20% is never computed here).
// ============================================================
import { ErrorNote } from '../shared/ActionBox'
import React, { useState } from 'react'
import DateInput from '../shared/DateInput'
import ModalFrame from '../shared/ModalFrame'
import { createDisbursementApi, updateDisbursementApi } from '../../api/casevoApi'
import { DISBURSEMENT_CATEGORIES, type Disbursement, type VatTreatment } from '../../types/billing.types'
import { BIcon, B_PRIMARY, B_SECONDARY, FIELD_ERROR, INPUT, LABEL, MoneyInput, NOTE, VAT_HELP, errorText, parsePounds, penceToPoundsText } from './billingUi'
import { isRealIsoDate, londonToday } from '../../utils/civilDate'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'

interface Props { caseId: string; existing?: Disbursement | null; onClose: () => void; onSaved: (d: Disbursement) => void }

const toPounds = penceToPoundsText // integer pence → "12.34" (no float)
// Today in UK time (not UTC — just after midnight in summer that's still yesterday).
const todayIso = (): string => londonToday()

const DisbursementModal: React.FC<Props> = ({ caseId, existing, onClose, onSaved }) => {
  const [description, setDescription] = useState(existing?.description ?? '')
  const [category, setCategory] = useState(existing?.category || 'medical')
  const [amount, setAmount] = useState(existing ? toPounds(existing.amountPence) : '')
  const [vat, setVat] = useState<VatTreatment>(existing?.vatTreatment ?? 'STANDARD')
  const [vatAmount, setVatAmount] = useState(existing && existing.vatTreatment === 'AT_COST' ? toPounds(existing.vatPence) : '')
  const [payableTo, setPayableTo] = useState(existing?.payableTo ?? '')
  const [paid, setPaid] = useState(existing ? existing.status === 'PAID' : true)
  // B3c — Casevo's DateInput (DD / MM / YYYY); a half-typed date holds Save.
  const [paidAt, setPaidAt] = useState<string | null>(existing?.paidAt ? existing.paidAt.slice(0, 10) : todayIso())
  const [paidAtTyping, setPaidAtTyping] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Unsaved-changes guard: ×, Esc, backdrop, Cancel (and leaving the screen /
  // Back / refresh) ask before dropping what was typed here.
  const guardClose = useUnsavedChangesGuard(useDraftDirty(true, [description, category, amount, vat, vatAmount, payableTo, paid, paidAt]) && !busy)
  const requestClose = (): void => guardClose(onClose)
  const net = parsePounds(amount)
  const vatP = vat === 'AT_COST' ? parsePounds(vatAmount) : 0
  const lockedMoney = !!existing?.onDraftInvoiceId
  // Paid on: a real date, 1950..today — the server refuses anything else (never a silent "now").
  const paidAtError = !paid || lockedMoney ? null
    : !paidAt || !isRealIsoDate(paidAt) ? 'Enter the date it was paid.'
      : paidAt > todayIso() ? 'The paid-on date can’t be in the future.'
        : paidAt < '1950-01-01' ? 'The paid-on date can’t be before 01/01/1950.' : null
  const valid = description.trim() !== '' && net != null && net > 0 && (vat !== 'AT_COST' || (vatP != null && vatP <= (net ?? 0))) && !paidAtError && !(paid && paidAtTyping)

  const pickCategory = (k: string): void => {
    setCategory(k)
    const c = DISBURSEMENT_CATEGORIES.find((x) => x.key === k)
    if (c) setVat(c.vat)
  }

  const save = async (): Promise<void> => {
    if (!valid || busy) return
    setBusy(true); setError(null)
    try {
      const body = {
        description: description.trim(), category, payableTo: payableTo.trim(),
        ...(lockedMoney ? {} : { amountPence: net as number, vatTreatment: vat, ...(vat === 'AT_COST' ? { vatPence: vatP as number } : {}) }),
        ...(lockedMoney ? {} : { status: paid ? 'PAID' as const : 'ANTICIPATED' as const, ...(paid ? { paidAt: new Date(`${paidAt as string}T12:00:00Z`).toISOString() } : {}) }),
      }
      const saved = existing ? await updateDisbursementApi(existing.id, body) : await createDisbursementApi(caseId, body)
      onSaved(saved)
    } catch (e) { setError(errorText(e, 'Couldn’t save the disbursement.')) } finally { setBusy(false) }
  }

  return (
    <ModalFrame onClose={requestClose} size="md" portal eyebrow={existing ? 'Edit disbursement' : 'New disbursement'} eyebrowIcon={<BIcon name="card" size={14} />}
      title={existing ? existing.description : 'Record a disbursement'} subtitle="A cost paid (or to be paid) on the client’s behalf. Paid disbursements can go on the next bill."
      footerRight={<>
        <button type="button" className={B_SECONDARY} onClick={requestClose}>Cancel</button>
        <button type="button" className={B_PRIMARY} disabled={!valid || busy} onClick={() => { void save() }}>{busy ? 'Saving…' : existing ? 'Save' : 'Add disbursement'}</button>
      </>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="sm:col-span-2"><span className={LABEL}>Description</span>
          <input className={INPUT} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} placeholder="e.g. Medical report — Dr A. Kaur" /></label>
        <label><span className={LABEL}>Type</span>
          <select className={INPUT} value={category} onChange={(e) => pickCategory(e.target.value)}>
            {DISBURSEMENT_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></label>
        <label><span className={LABEL}>Payable to</span>
          <input className={INPUT} value={payableTo} onChange={(e) => setPayableTo(e.target.value)} maxLength={200} placeholder="Supplier / court" /></label>
        <label><span className={LABEL}>Net amount</span>
          <MoneyInput value={amount} onChange={setAmount} disabled={lockedMoney} ariaLabel="Net amount"
            error={amount !== '' && (net == null || net <= 0) ? 'Enter the net amount, e.g. 540.00.' : null} /></label>
        <label><span className={LABEL}>VAT</span>
          <select className={INPUT} value={vat} onChange={(e) => setVat(e.target.value as VatTreatment)} disabled={lockedMoney}>
            {(['NONE', 'STANDARD', 'AT_COST'] as VatTreatment[]).map((t) => <option key={t} value={t}>{VAT_HELP[t][0]}</option>)}
          </select></label>
        {vat === 'AT_COST' && (
          <label className="sm:col-span-2"><span className={LABEL}>VAT on the supplier’s invoice</span>
            <MoneyInput value={vatAmount} onChange={setVatAmount} disabled={lockedMoney} ariaLabel="VAT on the supplier’s invoice"
              error={vatAmount === '' ? null : (vatP == null ? 'Enter the VAT, e.g. 70.00.' : net != null && vatP > net ? 'VAT can’t be more than the net amount.' : null)} />
            <span className={NOTE}>Passed on exactly — it must not be more than the net amount.</span></label>
        )}
        {vat !== 'AT_COST' && <p className={`${NOTE} sm:col-span-2 m-0`}>{VAT_HELP[vat][1]}. The VAT is calculated by Casevo.</p>}
        {!lockedMoney && (
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-[14px] text-casevo-forest"><input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="w-4 h-4 accent-[#2d6a4f]" />Paid</label>
            {paid && <div className="inline-flex items-center gap-2"><span className="text-[12px] font-semibold text-casevo-text-soft">Paid on</span><DateInput value={paidAt} onChange={setPaidAt} ariaLabel="Paid on" onValidityChange={(v) => setPaidAtTyping(!v)} /></div>}
            {paid && paidAtError && !paidAtTyping && <span role="alert" className={FIELD_ERROR}>{paidAtError}</span>}
            {!paid && <span className={NOTE}>Anticipated disbursements are held back from bills until they’re paid.</span>}
          </div>
        )}
        {lockedMoney && <p className={`${NOTE} sm:col-span-2 m-0`}>This disbursement is on a draft bill, so its amount and VAT are locked. Remove it from the draft to change them.</p>}
        {error && <ErrorNote className="sm:col-span-2">{error}</ErrorNote>}
      </div>
    </ModalFrame>
  )
}

export default DisbursementModal
